import { atomWithMutation, atomWithQuery } from "jotai-tanstack-query"
import { queryClient } from "../../api/constants"
import { atomFamily } from "jotai/utils"
import { PresetItem, PresetService } from "@/store/presets/presets-service"
import { atom } from "jotai/index"
import { allPresetsAtom, DEFAULT_PRESET_GROUP, userPresetsAtom } from "@/store/store-presets"
import { store } from "../store"
import { isAuthReadyAtom } from "@/store/auth-state"

const presetsQueryKey = ["presets"] as const

const sortByOrderScoreDesc = (presets: PresetItem[]) =>
	[...presets].sort((a, b) => b.orderScore - a.orderScore)

const updatePresetsCache = (
	updater: (current: PresetItem[] | undefined) => PresetItem[] | undefined,
) => {
	queryClient.setQueryData<PresetItem[]>(presetsQueryKey, updater)
}

const upsertPresetInList = (current: PresetItem[] | undefined, nextPreset: PresetItem) => {
	const withoutDuplicatedId = (current ?? []).filter((preset) => preset.id !== nextPreset.id)
	return sortByOrderScoreDesc([...withoutDuplicatedId, nextPreset])
}

export const fetchPresetsAtom = atomWithQuery<PresetItem[]>((get) => ({
	queryKey: presetsQueryKey,
	queryFn: PresetService.listPresets,
	refetchOnWindowFocus: false,
	enabled: get(isAuthReadyAtom),
}))

export const createPresetAtom = atomWithMutation(() => ({
	mutationKey: ["preset", "create"],
	mutationFn: PresetService.createPreset,
	onSuccess: (createdPreset) => {
		if (!createdPreset) {
			return
		}
		updatePresetsCache((current) => upsertPresetInList(current, createdPreset))
	},
}))

export const presetByIdMapAtom = atom((get) => {
	const { data: presets } = get(userPresetsAtom)
	return Object.fromEntries(presets.map((preset) => [preset.id, preset]))
})

export const presetByGroupMapAtom = atom((get) => {
	const { data: presets } = get(userPresetsAtom)
	const presetGroups: Record<string, PresetItem[]> = { "User Presets": [] }
	presets.map((preset) => {
		const group = preset.group ?? DEFAULT_PRESET_GROUP
		if (!presetGroups[group]) {
			presetGroups[group] = []
		}
		presetGroups[group]?.push(preset)
	})

	return presetGroups
})

export const presetGroupsAtom = atom((get) => {
	return Object.keys(get(presetByGroupMapAtom))
})

export const updatePresetAtomFamily = atomFamily((presetId?: string) =>
	atomWithMutation((get) => ({
		mutationKey: ["preset", "update", presetId],
		mutationFn: async (mutations: Partial<PresetItem>) => {
			if (!presetId) {
				throw new Error("Cannot update preset without a target ID.")
			}
			const preset = get(presetByIdMapAtom)[presetId]
			if (!preset) {
				throw new Error(`Cannot update preset: no preset found for id ${presetId}.`)
			}
			return PresetService.updatePreset(preset, mutations)
		},
		onSuccess: (updatedPreset: PresetItem) => {
			updatePresetsCache((current) => upsertPresetInList(current, updatedPreset))
		},
	})),
)

export const deletePresetAtomFamily = atomFamily((presetId?: string) =>
	atomWithMutation((get) => ({
		mutationKey: ["preset", "delete", presetId],
		mutationFn: async () => {
			if (!presetId) {
				throw new Error("Cannot delete preset")
			}

			const preset = get(presetByIdMapAtom)[presetId]
			if (preset) {
				return PresetService.deletePreset(preset)
			}
		},
		onSuccess: () => {
			if (!presetId) {
				return
			}
			updatePresetsCache((current) => current?.filter((preset) => preset.id !== presetId))
		},
	})),
)

export const setupPresetsSubscribers = () => {
	store.sub(allPresetsAtom, () => {
		//This is to keep projects up to date throughout the entire app
	})
}
